import { apiFetch } from './utils/api';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ConfirmationModal } from './components/ui/ConfirmationModal';
import ProjectProgressBar from './components/ProjectProgressBar';
import { ExportModal } from './components/modals/ExportModal';
import { ImportModal } from './components/modals/ImportModal';
import { SyncModal } from './components/ui/SyncModal';
import { CopyProductModal } from './components/modals/CopyProductModal';
import { CreateProductModal } from './components/modals/CreateProductModal';
import { usePIM } from './context/PIMContext';
import { useToast } from './context/ToastContext';
import { PropertiesTab } from './components/tabs/PropertiesTab';
import { ChargesTab } from './components/tabs/ChargesTab';
import { LanguagesTab } from './components/tabs/LanguagesTab';
import { PackagingLogisticsTab } from './components/tabs/PackagingLogisticsTab';
import PicturesTab from './components/tabs/PicturesTab';
import { AddressTab } from './components/tabs/AddressTab';
import { SoftwareTab } from './components/tabs/SoftwareTab';
import { DocumentsTab } from './components/tabs/DocumentsTab';
import { ToDoListTab } from './components/tabs/ToDoListTab';
import AccessoriesTab from './components/tabs/AccessoriesTab';
import ComponentsTab from './components/tabs/ComponentsTab';
import UserManagementTab from './components/UserManagementTab';
import AdminPanel from './components/admin/AdminPanel';
import { AiAssistant } from './components/ai/AiAssistant';
import { AiAssistantTab } from './components/ai/AiAssistantTab';
import { ReturnsTab } from './components/tabs/ReturnsTab';
import ReactMarkdown from 'react-markdown';
import { Package, User, Power, Save, Copy, Eraser, Trash, Download, FileInput, FileSpreadsheet, ChevronLeft, ChevronRight, Plus, ChevronUp, ChevronDown, List, Play, Info, RefreshCw, HardDrive, CheckSquare, Mic, Sparkles, X, LayoutGrid, Eye, Pencil, Lock, Globe } from 'lucide-react';
import { GhostButton } from './components/ui/GhostButton';
import { ProductListView } from './components/ProductListView';
import { renderWidget } from './components/WidgetRegistry';
import GlobalTasksView from './components/GlobalTasksView';
import SalesDashboard from './components/views/SalesDashboard'; // Import Dashboard
import { Login } from './components/auth/Login';
import { HandbookProvider, useHandbook } from './context/HandbookContext'; // Import Context
import ParticleRing from './components/auth/ParticleRing';

// System Fade-In Animation
const systemFadeInStyle = `
  @keyframes system-fade-in {
    from { opacity: 0; transform: scale(0.98); filter: blur(10px); }
    to { opacity: 1; transform: scale(1); filter: blur(0px); }
  }
  .system-fade-in {
    animation: system-fade-in 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

// Flip Panel Component
const FlipPanel = ({ isFlipped, front, back }) => {
  return (
    <div className="relative w-full h-full" style={{ perspective: '1000px' }}>
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        <div className="relative w-full h-full" style={{ backfaceVisibility: 'hidden' }}>{front}</div>
        <div className="absolute inset-0 w-full h-full" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>{back}</div>
      </div>
    </div>
  );
};

// --- EDITOR COMPONENT (Old App Content) ---
const Editor = () => {
  const {
    activeTab, setActiveTab, logout, saveProduct,
    newProduct, clearProduct, deleteProduct, loadProducts,
    loadedProducts, currentProductIndex, nextProduct, prevProduct,
    productData, setProductData, activeCharge,
    currentUser, setCurrentUser,
    leftWidget, middleWidget, rightWidget,
    isPalletMode, setIsPalletMode, palletMiddleWidget, palletRightWidget,
    canEditContent, canExport
  } = usePIM();

  const { toast } = useToast();
  const navigate = useNavigate();
  const { openHandbook } = useHandbook(); // Use Hook

  const [isTasksViewOpen, setIsTasksViewOpen] = React.useState(false);
  const [isAiOpen, setIsAiOpen] = React.useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = React.useState(false);

  // Expose openHandbook to window for deep linking from other components if needed (or use Context)
  // For now, simpler to pass down or just use global event, but let's keep it local.
  // Ideally, usePIM context should hold this, but local is fine for prompt scope.

  const [isListViewOpen, setIsListViewOpen] = React.useState(false);
  const [isDescriptionFlipped, setIsDescriptionFlipped] = React.useState(false);
  const [isAiChatFlipped, setIsAiChatFlipped] = React.useState(false);
  const [storageStatus, setStorageStatus] = React.useState(true);
  const [isGeneratingSEO, setIsGeneratingSEO] = React.useState(false);
  const [isDescriptionPreview, setIsDescriptionPreview] = React.useState(true);

  // ... (Hooks and Handlers)
  const handleGenerateSEO = async () => {
    if (!productData?.id) { toast.error("Please save the product first."); return; }
    setIsGeneratingSEO(true);
    try {
      const res = await fetch('/api/ki/generate', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify({ product_id: productData.id }) });
      if (!res.ok) throw new Error("Generation failed");
      const data = await res.json();
      setProductData(prev => ({ ...prev, description: data.response }));
    } catch (error) {
      console.error("SEO Generation Error:", error);
      toast.error("Failed to generate SEO description.");
    } finally { setIsGeneratingSEO(false); }
  };

  React.useEffect(() => {
    const checkStorage = async () => {
      try {
        const res = await apiFetch('/api/storage-status');
        if (res.ok) {
          const data = await res.json();
          setStorageStatus(data.online ?? data.storageMounted ?? true);
        } else {
          setStorageStatus(false);
        }
      } catch {
        setStorageStatus(false);
      }
    };
    checkStorage();
    const interval = setInterval(checkStorage, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    const success = await saveProduct();
    if (!success) toast.error('Failed to save product.');
    else toast.success('Product saved successfully.');
  };

  const tabs = [
    { id: 'properties', label: 'Properties' },
    { id: 'languages', label: 'Languages' },
    { id: 'pictures', label: 'Pictures' },
    { id: 'documents', label: 'Documents' },
    { id: 'software', label: 'Software & Res.' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'components', label: 'Components' },
    { id: 'packaging', label: 'Packaging Logistics' },
    { id: 'charges', label: 'Charges' },
    { id: 'address', label: 'Address' },
    { id: 'todo', label: 'Task' },
    { id: 'ai-assistant', label: 'AI Assistant', restricted: true },
    { 
      id: 'settings', 
      label: 'Settings', 
      restricted: true,
      subMenu: [
        { id: 'users', label: 'User Management' },
        { id: 'ai-admin', label: 'AI Settings' }
      ]
    },
  ];
  const visibleTabs = tabs.filter(tab => !tab.restricted || currentUser?.role === 'Admin');

  const renderContent = () => {
    switch (activeTab) {
      case 'properties': return <PropertiesTab />;
      case 'charges': return <ChargesTab />;
      case 'languages': return <LanguagesTab />;
      case 'packaging': return <PackagingLogisticsTab />;
      case 'pictures': return <PicturesTab />;
      case 'address': return <AddressTab />;
      case 'todo': return <ToDoListTab />;
      case 'software': return <SoftwareTab />;
      case 'documents': return <DocumentsTab />;
      case 'accessories': return <AccessoriesTab />;
      case 'components': return <ComponentsTab />;
      case 'users': return <UserManagementTab />;
      case 'returns': return <ReturnsTab />;
      case 'ai-assistant': return <AiAssistantTab />;
      case 'ai-admin': return <AdminPanel />;
      default: return <div className="text-stone-500 p-10 text-center">Tab "{activeTab}" not yet implemented.</div>;
    }
  };

  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const handleDeleteClick = () => setIsDeleteModalOpen(true);
  const handleConfirmDelete = async () => { await deleteProduct(); setIsDeleteModalOpen(false); };
  const [isExportModalOpen, setIsExportModalOpen] = React.useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
  const handleExportClick = () => setIsExportModalOpen(true);
  const handleImportClick = () => setIsImportModalOpen(true);
  const [isCopyModalOpen, setIsCopyModalOpen] = React.useState(false);
  const handleCopyClick = () => setIsCopyModalOpen(true);
  const handleImport = (d) => { console.log(d); toast.info("Import functionality from main page is not fully implemented yet."); };

  const [isPublishModalOpen, setIsPublishModalOpen] = React.useState(false);
  const [isPublishing, setIsPublishing] = React.useState(false);

  const handlePublishClick = () => {
    if (!productData?.sku) {
        toast.error("No product loaded to publish.");
        return;
    }
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = async () => {
    setIsPublishModalOpen(false);
    setIsPublishing(true);
    toast.info(`Publishing ${productData.sku}...`);
    try {
      const res = await fetch(`/api/v1/publish/product/${productData.sku}`, {
        method: 'POST',
        
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Product ${productData.sku} published successfully!`);
      } else {
        toast.error(`Publish failed: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(`Publish request failed: ${err.message}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const [isSyncModalOpen, setIsSyncModalOpen] = React.useState(false);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [syncResult, setSyncResult] = React.useState(null);

  const handleSyncClick = async () => {
    setIsSyncModalOpen(true);
    setIsSyncing(true);
    setSyncResult(null);

    try {
      const res = await apiFetch('/api/sync-files', { method: 'POST' });
      const result = await res.json();
      setSyncResult(result);
      if (result.success) toast.success(`Sync complete. Synced: ${result.synced}`); else toast.error(`Sync failed: ${result.message}`);
    } catch {
      setSyncResult({ success: false, message: "Sync request failed. The server might be unreachable or timed out." });
      toast.error("Sync request failed");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-transparent relative text-[var(--color-text-primary)] font-sans selection:bg-orange-500/30">
        {/* Subtle Glow Background matching microangelo.eu */}
        
      <ConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleConfirmDelete} title="Delete Product" message="Are you sure?" />
      <ExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} filters={{ sku: productData?.sku }} count={1} selectedProductIds={productData?.id ? [productData.id] : []} />
      <ImportModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImport={handleImport} />
      <SyncModal isOpen={isSyncModalOpen} onClose={() => setIsSyncModalOpen(false)} isSyncing={isSyncing} syncResult={syncResult} />
      <CopyProductModal isOpen={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} sourceProduct={productData} activeCharge={activeCharge} />
      <CreateProductModal isOpen={isCreateProductModalOpen} onClose={() => setIsCreateProductModalOpen(false)} />
      <ConfirmationModal isOpen={isPublishModalOpen} onClose={() => setIsPublishModalOpen(false)} onConfirm={handleConfirmPublish} title="Publish to Live Website" message={`Are you sure you want to publish ${productData?.sku} to the live website?`} />

      {/* HANDBOOK MODAL */}
      {/* Modal is now rendered by Provider, but if we kept it here we'd double render. 
          The Provider renders it. So remove <HandbookModal /> from here. 
      */}

      {/* TOP BAR */}
      <div className="bg-black border-b border-stone-800 h-10 shrink-0 flex items-center justify-between px-10 select-none">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 rounded p-0.5"><Package size={12} className="text-white" /></div>
          <span className="font-bold text-stone-200 text-sm tracking-wide">PIM System</span>
        </div>
        <div className="flex items-center gap-5">
          {/* HANDBOOK ENTRY POINT */}
          <button onClick={() => openHandbook('overview')} className="flex items-center gap-2 px-3 py-1 text-xs font-medium text-stone-400 hover:text-white transition" title="PIM Handbook">
            <span style={{ fontSize: '1.2em' }}>📘</span> Handbook
          </button>
          <div className="w-px h-4 bg-stone-800" />

          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-3 py-1 rounded-sm text-xs font-medium text-stone-400 hover:text-white hover:bg-stone-800 transition"
          >
            <LayoutGrid size={12} /> Dashboard
          </button>
          <div className="w-px h-4 bg-stone-800" />

          <div className="flex items-center gap-2 text-sm" title={storageStatus ? "Storage Online" : "Storage Offline"}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border ${storageStatus ? 'bg-green-900/30 border-green-700 text-green-400' : 'bg-orange-900/30 border-orange-700 text-orange-400'}`}><HardDrive size={12} /></div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center text-xs text-stone-300 border border-stone-700"><User size={12} /></div>
            <span className="text-stone-300 font-medium text-sm">{currentUser?.role || 'Viewer'}</span>
          </div>
          <GhostButton icon={Sparkles} label="Agent" onClick={() => setIsAiOpen(!isAiOpen)} className={`transition-all duration-300 ${isAiOpen ? 'bg-purple-600 text-white shadow-[0_0_15px_rgba(147,51,234,0.5)]' : 'text-purple-300 hover:text-white hover:bg-purple-900/40'}`} />
          <div className="w-px h-8 bg-stone-800 mx-2" />
          <button onClick={logout} className="text-stone-500 hover:text-red-400 transition text-sm"><Power size={14} /></button>
        </div>
      </div>
      <AiAssistant isOpen={isAiOpen} onClose={() => setIsAiOpen(false)} />

      {/* TOOLBAR */}
      <header className="bg-[var(--color-bg-panel)] border-b border-[var(--color-border)] px-4 lg:px-10 py-3 flex justify-between items-center shadow-md min-h-16 shrink-0 transition-colors duration-300 overflow-x-auto gap-4">
        <div className="flex space-x-2">
          {canEditContent && <GhostButton onClick={() => setIsCreateProductModalOpen(true)}><Plus size={16} className="text-blue-400" /> New</GhostButton>}
          {canEditContent && <GhostButton onClick={handleSave}><Save size={16} className="text-green-400" /> Save</GhostButton>}
          {canEditContent && <GhostButton onClick={handleCopyClick}><Copy size={16} className="text-stone-400" /> Copy</GhostButton>}
          {canEditContent && <GhostButton onClick={clearProduct}><Eraser size={16} className="text-stone-400" /> Clear</GhostButton>}
          {canEditContent && <GhostButton onClick={handleDeleteClick} className="hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10"><Trash size={16} /> Delete</GhostButton>}
          <GhostButton onClick={() => loadProducts(false)}><Download size={16} /> Load</GhostButton>
          <GhostButton onClick={() => setIsListViewOpen(true)}><List size={16} /> List</GhostButton>
          <GhostButton onClick={() => setIsTasksViewOpen(true)}><CheckSquare size={16} /> Tasks</GhostButton>
          {currentUser?.role === 'Admin' && (
            <GhostButton 
              onClick={handlePublishClick} 
              className={`border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 ${isPublishing ? 'animate-pulse opacity-50' : ''}`}
              disabled={isPublishing}
            >
              <Globe size={16} /> {isPublishing ? 'Publishing...' : 'Publish'}
            </GhostButton>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-stone-800 rounded border border-stone-600 flex items-center h-9 min-w-[140px] overflow-hidden">
            <button onClick={prevProduct} className="h-full px-3 hover:bg-stone-700 border-r border-stone-600 transition-colors text-stone-400 hover:text-white"><ChevronLeft size={16} /></button>
            <div className="flex-1 flex items-center justify-center text-xs font-mono text-stone-400 px-2 select-none">{loadedProducts.length > 0 ? `${currentProductIndex + 1} / ${loadedProducts.length}` : '0 / 0'}</div>
            <button onClick={nextProduct} className="h-full px-3 hover:bg-stone-700 border-l border-stone-600 transition-colors text-stone-400 hover:text-white"><ChevronRight size={16} /></button>
          </div>
          {canEditContent && <GhostButton onClick={handleSyncClick}><RefreshCw size={16} /> Sync</GhostButton>}
          {canEditContent && <GhostButton onClick={handleImportClick}><FileInput size={16} /> Import</GhostButton>}
          {canExport && <GhostButton onClick={handleExportClick}><FileSpreadsheet size={16} /> Export</GhostButton>}
        </div>
      </header>

      <div className="px-10 pt-[5px] pb-0"><ProjectProgressBar /></div>

      {/* MAIN LAYOUT */}
      <div className={`flex-none transition-all duration-500 ease-in-out border-b border-stone-700 ${isHeaderCollapsed ? 'max-h-0 opacity-0 border-none overflow-hidden' : 'h-auto opacity-100'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 h-full px-4 lg:px-10 pt-2 pb-6">
          <div className="col-span-1 lg:col-span-4 rounded-2xl overflow-hidden border border-white/10 shadow-xl p-2 relative group" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg-panel), transparent 60%)' }}>
            <button onClick={() => { if (isPalletMode) setIsPalletMode(false); setIsDescriptionFlipped(!isDescriptionFlipped); }} className="absolute top-2 right-2 z-10 p-1.5 rounded transition-colors text-stone-500 hover:text-white hover:bg-stone-700/50">{isDescriptionFlipped ? <X size={16} /> : <Info size={16} />}</button>
            <FlipPanel isFlipped={isDescriptionFlipped} front={renderWidget(leftWidget)} back={
              <div className="h-full flex flex-col relative p-2">
                <h3 className="absolute top-0 left-1 text-lg font-bold text-white z-10">Description</h3>

                {/* Toolbar */}
                <div className="absolute top-2 right-12 z-20 flex items-center gap-2">
                  {canEditContent && (
                    <button onClick={() => setIsDescriptionPreview(!isDescriptionPreview)} className="flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium border bg-stone-800 border-stone-600 text-stone-300 hover:text-white transition">
                      {isDescriptionPreview ? <><Pencil size={12} /> Edit</> : <><Eye size={12} /> Preview</>}
                    </button>
                  )}
                  {canEditContent && (
                    <button
                      onClick={handleGenerateSEO}
                      disabled={isGeneratingSEO}
                      className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium border transition-all min-w-[105px] ${isGeneratingSEO ? 'bg-purple-900/20 border-purple-800 cursor-not-allowed text-purple-200' : 'bg-purple-900/30 border-purple-700 hover:bg-purple-900/50 text-purple-200 hover:text-white'}`}
                    >
                      {isGeneratingSEO ? (
                        <div className="flex gap-1 items-center h-4">
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"></div>
                        </div>
                      ) : (
                        "Generate SEO"
                      )}
                    </button>
                  )}
                </div>

                <div className="h-10 shrink-0" />

                {(isDescriptionPreview || !canEditContent) ? (
                  <div className="flex-1 w-full bg-stone-900/50 border border-stone-700 rounded-lg p-4 overflow-y-auto">
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown>{productData?.description || '*No description yet.*'}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <textarea
                    className="flex-1 w-full bg-stone-900/50 border border-stone-700 rounded-lg p-4 text-stone-300 resize-none focus:outline-none focus:border-blue-500 font-mono text-sm leading-relaxed"
                    value={productData?.description || ''}
                    onChange={(e) => setProductData({ ...productData, description: e.target.value })}
                    placeholder="Enter product description..."
                  />
                )}

                {/* Internal Factory Model Field */}
                <div className="mt-3 pt-3 border-t border-stone-700 flex items-center gap-3">
                  <div className="text-xs font-bold text-stone-500 uppercase flex items-center gap-1 min-w-[100px]" title="Internal Factory Model Code">
                    <Lock size={12} /> Factory Code:
                  </div>
                  {canEditContent ? (
                    <input
                      type="text"
                      className="flex-1 bg-stone-900/30 border border-stone-700 rounded px-2 py-1.5 text-xs text-stone-300 placeholder-stone-600 focus:outline-none focus:border-blue-500 font-mono tracking-wider"
                      value={productData?.factory_model || ''}
                      onChange={(e) => setProductData({ ...productData, factory_model: e.target.value })}
                      placeholder="e.g. D111 (Internal)"
                    />
                  ) : (
                    <span className="flex-1 text-xs text-stone-300 font-mono tracking-wider px-2 py-1.5">
                      {productData?.factory_model || '-'}
                    </span>
                  )}
                </div>
              </div>
            } />
          </div>
          <div className="col-span-1 lg:col-span-4 rounded-2xl overflow-hidden border border-white/10 shadow-xl p-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg-panel), transparent 60%)' }}><FlipPanel isFlipped={isPalletMode} front={renderWidget(middleWidget)} back={renderWidget(palletMiddleWidget)} /></div>
          <div className="col-span-1 lg:col-span-4 rounded-2xl overflow-hidden border border-white/10 shadow-xl p-4 relative group" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg-panel), transparent 60%)' }}>
            {!isPalletMode && <button onClick={() => setIsAiChatFlipped(!isAiChatFlipped)} className="absolute top-2 right-2 z-10 p-1.5 rounded text-stone-500 hover:text-white hover:bg-stone-700/50">{isAiChatFlipped ? <X size={16} /> : <Info size={16} />}</button>}
            <FlipPanel isFlipped={isPalletMode || isAiChatFlipped} front={renderWidget(rightWidget)} back={isPalletMode ? renderWidget(palletRightWidget) : <div className="h-full flex flex-col relative p-2"><AiAssistant embedded={true} isOpen={true} /></div>} />
          </div>
        </div>
      </div>

      {/* INFO BAR */}
      <div className={`flex-none bg-[var(--color-bg-panel)] border-b border-[var(--color-border)] px-10 flex items-center gap-8 shadow-sm overflow-hidden transition-all duration-500 ease-in-out ${isHeaderCollapsed ? 'h-12 opacity-100' : 'h-0 opacity-0 border-none'}`}>
        <div className="flex items-center gap-2"><span className="text-xs font-bold text-stone-500 uppercase">MODEL:</span><span className="text-stone-200 font-medium">{productData?.model || '-'}</span></div>
        <div className="flex items-center gap-2"><span className="text-xs font-bold text-stone-500 uppercase">CHARGE:</span><span className="text-stone-200 font-medium">{activeCharge?.charge_id || 'No Charge'}</span></div>
        <div className="flex items-center gap-2"><span className="text-xs font-bold text-stone-500 uppercase">SKU:</span><span className="text-stone-200 font-medium">{productData?.sku || '-'}</span></div>
      </div>

      {/* TABS */}
      <nav className="bg-[var(--color-bg-surface)] border-y border-[var(--color-border)] h-12 shrink-0 flex overflow-x-auto z-20 px-10">
        {visibleTabs.map(tab => {
          const isActive = activeTab === tab.id || (tab.subMenu && tab.subMenu.some(s => s.id === activeTab));
          const defaultTabId = tab.subMenu ? tab.subMenu[0].id : tab.id;
          return (
            <div key={tab.id} onClick={() => setActiveTab(defaultTabId)} className={`px-4 flex items-center text-sm whitespace-nowrap transition-colors border-r border-[var(--color-border)] cursor-pointer select-none h-full ${isActive ? 'bg-[var(--color-bg-panel)] text-[var(--color-text-primary)] border-b-2 border-b-blue-600 font-bold' : 'text-stone-200 font-normal hover:text-white hover:font-bold hover:bg-[var(--color-bg-panel)] border-b-2 border-b-transparent'}`}>
              <div className="relative flex flex-col items-center justify-center">
                <span className="invisible h-0 font-bold overflow-hidden pointer-events-none" aria-hidden="true">{tab.label}</span>
                <span className="absolute inset-0 flex items-center justify-center">{tab.label}</span>
              </div>
            </div>
          );
        })}
        <div className="flex-1 border-b border-stone-700"></div>
        <button onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)} className="px-4 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] border-l border-[var(--color-border)] transition flex items-center justify-center">{isHeaderCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}</button>
      </nav>
      {/* SUB-MENU TABS */}
      {(() => {
        const activeMenu = visibleTabs.find(t => t.id === activeTab || (t.subMenu && t.subMenu.some(s => s.id === activeTab)));
        if (activeMenu && activeMenu.subMenu) {
          return (
            <div className="bg-[var(--color-bg-panel)] border-b border-[var(--color-border)] h-10 shrink-0 flex px-10 items-center gap-6 overflow-x-auto shadow-inner">
              {activeMenu.subMenu.map(sub => (
                <div key={sub.id} onClick={() => setActiveTab(sub.id)} className={`text-sm font-medium cursor-pointer transition-colors h-full flex items-center px-2 ${activeTab === sub.id ? 'text-[var(--color-text-primary)] border-b-2 border-blue-500' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] border-b-2 border-transparent'}`}>
                  {sub.label}
                </div>
              ))}
            </div>
          );
        }
        return null;
      })()}

      <div className="flex-1 bg-transparent flex flex-col min-h-0"><div className="flex-1 px-10 py-3 relative flex flex-col min-h-0">{renderContent()}</div></div>
      {isListViewOpen && <ProductListView onClose={() => setIsListViewOpen(false)} />}
      {isTasksViewOpen && <GlobalTasksView onClose={() => setIsTasksViewOpen(false)} />}
    </div>
  );
};

// --- APP ROOT WITH ROUTING ---
import ImageReviewPanel from './components/admin/ImageReviewPanel';
const App = () => {
  const { currentUser, authLoading, ringState, updateRingState } = usePIM();
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);
  const [showRing, setShowRing] = React.useState(() => {
    // Immediate check to prevent flash on refresh
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    return !(token && storedUser);
  });
  const wasAlreadyLoggedIn = React.useRef(false);

  React.useEffect(() => {
    if (!authLoading && !hasCheckedAuth) {
      if (currentUser) {
        wasAlreadyLoggedIn.current = true;
      }
      setHasCheckedAuth(true);
    }
  }, [authLoading, currentUser, hasCheckedAuth]);

  // Handle Ring Unmounting after login transition
  React.useEffect(() => {
    if (currentUser && !wasAlreadyLoggedIn.current) {
        // Transition is 1.5s, we give it a bit more
        const timer = setTimeout(() => setShowRing(false), 3000);
        return () => clearTimeout(timer);
    } else if (currentUser && wasAlreadyLoggedIn.current) {
        // User already authenticated - ensure ring is unmounted
        setShowRing(false);
    } else if (!currentUser && hasCheckedAuth) {
        // Logged out or no user - show ring for login screen
        wasAlreadyLoggedIn.current = false; // Reset for next login animation
        setShowRing(true);
    }
  }, [currentUser, hasCheckedAuth]);

  // Central Flare Management (1.5s hold, 5s fade)
  React.useEffect(() => {
    if (!ringState || ringState.flareIntensity <= 0) return;

    let frameId;
    let lastTime = performance.now();
    
    const fade = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const timeSinceTrigger = Date.now() - (ringState.flareTriggerTime || 0);
      
      if (timeSinceTrigger < 1500) {
        // Hold full brightness
        frameId = requestAnimationFrame(fade);
      } else {
        // Fade slowly over 5 seconds
        const reduction = deltaTime / 5000;
        const nextIntensity = Math.max(0, ringState.flareIntensity - reduction);
        
        if (Math.abs(nextIntensity - ringState.flareIntensity) > 0.0001) {
            updateRingState({ flareIntensity: nextIntensity });
        }
        
        if (nextIntensity > 0) {
            frameId = requestAnimationFrame(fade);
        }
      }
    };

    frameId = requestAnimationFrame(fade);
    return () => cancelAnimationFrame(frameId);
  }, [ringState?.flareIntensity, ringState?.flareTriggerTime, updateRingState]);

  if (authLoading || !hasCheckedAuth) {
    return (
      <div className="h-screen w-full bg-[#0c0a09] flex items-center justify-center text-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-stone-400">Loading PIM System...</p>
        </div>
      </div>
    );
  }



  return (
    <div className="relative min-h-screen bg-transparent overflow-hidden">
      {/* Login Background Radial Gradient */}
      {showRing && (
        <div 
          className={`fixed inset-0 pointer-events-none transition-opacity duration-[1500ms] ${currentUser ? 'opacity-0' : 'opacity-100'}`}
          style={{
            background: 'radial-gradient(circle at center, #020c2c 0%, #00071e min(260px, 30vw), #00071e 100%)'
          }}
        />
      )}

        <style>{systemFadeInStyle}</style>
        
        {/* Persistent Background Particle Ring (only during login/transition) */}
        {showRing && (
            <div className="fixed inset-0 z-0 pointer-events-none">
                <ParticleRing 
                    targetRadiusMultiplier={ringState?.targetRadiusMultiplier || 1.0}
                    flareIntensity={ringState?.flareIntensity || 0}
                    flareColor={ringState?.flareColor || [220, 20, 20]}
                    isSuccess={ringState?.isSuccess || false}
                />
            </div>
        )}

        <div className="relative z-10 w-full h-screen overflow-hidden">
            {/* Login Layer: Cross-fades away when currentUser appears */}
            {(!currentUser || !wasAlreadyLoggedIn.current) && (
                <div className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out z-20 ${currentUser ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    <Login />
                </div>
            )}

            {/* Dashboard Layer: Fades in when currentUser appears */}
            {currentUser && (
                <div className={`${wasAlreadyLoggedIn.current ? 'opacity-100' : 'system-fade-in'} absolute inset-0 z-10 overflow-hidden`}>
                    <HandbookProvider>
                        <Router>
                            <Routes>
                                <Route path="/dashboard" element={<SalesDashboard />} />
                                <Route path="/admin/image-review" element={<ImageReviewPanel />} />
                                <Route path="*" element={<Editor />} />
                            </Routes>
                        </Router>
                    </HandbookProvider>
                </div>
            )}
        </div>
    </div>
  );
};

export default App;

